import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, RotateCcw, Loader2, Film } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VideoBox = ({ label, url, testId }) => (
  <div className="flex-1 min-w-0">
    <p className="text-xs font-semibold text-slate-500 mb-1">{label}</p>
    {url ? (
      <video src={url} controls muted loop playsInline className="w-full aspect-video rounded-lg bg-black" data-testid={testId} />
    ) : (
      <div className="w-full aspect-video rounded-lg bg-slate-100 flex items-center justify-center text-xs text-slate-400" data-testid={testId}>
        Aucune vidéo
      </div>
    )}
  </div>
);

const ReviewItem = ({ item, busy, onAction }) => (
  <div className="border border-slate-200 rounded-xl p-3" data-testid={`video-review-item-${item.id}`}>
    <p className="font-semibold text-slate-800 text-sm mb-2">
      {item.year} {item.make} {item.model} {item.trim || ''}
      <span className="text-slate-400 font-normal ml-2">#{item.stock_number || '—'}</span>
    </p>
    <div className="flex flex-col sm:flex-row gap-3">
      <VideoBox label="Vidéo actuelle (en ligne)" url={item.video_url} testId={`video-review-current-${item.id}`} />
      <VideoBox label="Nouvelle vidéo IA" url={item.video_pending_url} testId={`video-review-new-${item.id}`} />
    </div>
    <div className="flex flex-wrap gap-2 mt-3 justify-end">
      <Button size="sm" variant="outline" disabled={busy} onClick={() => onAction(item, 'reject')} data-testid={`video-reject-${item.id}`}>
        <X className="h-4 w-4 mr-1" /> Rejeter
      </Button>
      <Button size="sm" variant="outline" disabled={busy} onClick={() => onAction(item, 'retry')} className="text-purple-700 border-purple-200" data-testid={`video-reject-retry-${item.id}`}>
        <RotateCcw className="h-4 w-4 mr-1" /> Rejeter + réessayer
      </Button>
      <Button size="sm" disabled={busy} onClick={() => onAction(item, 'approve')} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`video-approve-${item.id}`}>
        <Check className="h-4 w-4 mr-1" /> Approuver
      </Button>
    </div>
  </div>
);

export const VideoReviewDialog = ({ open, onOpenChange, vehicleId, getAuthHeader, onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/videos/pending`, { headers: getAuthHeader() });
      setItems(vehicleId ? res.data.filter(v => v.id === vehicleId) : res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open, vehicleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onAction = async (item, action) => {
    if (action === 'retry' && !window.confirm('Rejeter et générer une nouvelle vidéo? (~235 crédits PixVerse)')) return;
    setBusy(true);
    try {
      const url = action === 'approve'
        ? `${API}/admin/vehicles/${item.id}/video/approve`
        : `${API}/admin/vehicles/${item.id}/video/reject?retry=${action === 'retry'}`;
      await axios.post(url, {}, { headers: getAuthHeader() });
      toast.success(action === 'approve' ? 'Vidéo approuvée — maintenant en ligne' : action === 'retry' ? 'Rejetée — nouvelle vidéo en génération' : 'Vidéo rejetée');
      setItems(prev => prev.filter(v => v.id !== item.id));
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec');
    } finally {
      setBusy(false);
    }
  };

  const approveAll = async () => {
    if (!window.confirm(`Approuver les ${items.length} vidéo(s) en attente?`)) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/videos/approve-all`, {}, { headers: getAuthHeader() });
      toast.success(`${res.data.approved} vidéo(s) approuvée(s)`);
      setItems([]);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="video-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Film className="h-5 w-5 text-purple-600" /> Vidéos à approuver ({items.length})
          </DialogTitle>
          <DialogDescription>Les nouvelles vidéos IA ne sont visibles en ligne qu'après approbation.</DialogDescription>
        </DialogHeader>
        {!vehicleId && items.length > 1 && (
          <div className="flex justify-end">
            <Button size="sm" disabled={busy} onClick={approveAll} className="bg-emerald-600 hover:bg-emerald-700" data-testid="video-approve-all-btn">
              <Check className="h-4 w-4 mr-1" /> Tout approuver
            </Button>
          </div>
        )}
        {loading ? (
          <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-purple-500" /></div>
        ) : items.length === 0 ? (
          <p className="py-10 text-center text-slate-500 text-sm" data-testid="video-review-empty">Aucune vidéo en attente</p>
        ) : (
          <div className="space-y-4">
            {items.map(item => <ReviewItem key={item.id} item={item} busy={busy} onAction={onAction} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
